import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import {
    delay,
    distinctUntilChanged,
    mapTo,
    share,
    startWith,
    switchMap,
    throttle
} from 'rxjs/operators';
import { Color } from '../../../constants/color.constants';
import { Icons } from '../../../constants/icons.constants';
import { ActionSheet } from '../../../models/common';
import { ActionSheetService } from '../../../services/action-sheet/action-sheet.service';
import { BackdropService } from '../../../services/backdrop/backdrop.service';
import { HideOpenElementsService } from '../../../services/hide-open-elements/hide-open-elements.service';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';

export const enum ActionSheetAnimationState {
    Open = 'open',
    OpenTop = 'openTop',
    Closed = 'closed'
}

/**
 * @export
 * @class LsqActionSheetComponent
 */
@LifecycleAware()
@Component({
    selector: 'lsq-action-sheet',
    template: `
        <div
            class="lsq-action-sheet-content"
            [ngStyle]="{
                visibility: (actionSheetIsFullyClosed$ | async) ? 'hidden' : 'visible',
                height: (actionSheetIsFullyClosed$ | async) ? '0' : 'auto'
            }"
            [@slide]="animationState$ | async"
            (@slide.done)="animationDoneSubject.next()"
        >
            <!-- Using 'visibility' rather than 'display' because Safari messes up the
             closed => * animation if this element isn't in the layout flow. -->

            <ng-container [ngTemplateOutlet]="actionSheet?.templateRef"></ng-container>
            <lsq-button
                *ngIf="actionSheet?.showCloseButton"
                class="lsq-action-sheet-close-btn"
                ariaLabel="Close action sheet"
                appearance="transparent"
                (buttonClick)="handleCloseClick()"
            >
                <lsq-icon [type]="icons.Close" [color]="actionSheet?.closeButtonColor"></lsq-icon>
            </lsq-button>

            <div *ngIf="!actionSheet && isLoading" class="lsq-action-sheet-loading">
                Loading...
            </div>
        </div>
    `,
    styleUrls: ['./lsq-action-sheet.component.scss'],
    animations: [
        trigger('slide', [
            state('open', style({ top: '*' })),
            state('openTop', style({ top: '0' })),
            state('closed', style({ top: '100%' })),
            transition('* <=> closed', animate('250ms cubic-bezier(0,.65,.26,1)'))
        ])
    ]
})
export class LsqActionSheetComponent implements OnInit, OnDestroy {
    private _animationStateSubject = new BehaviorSubject<ActionSheetAnimationState>(
        ActionSheetAnimationState.Closed
    );
    public icons = Icons;
    public color = Color;
    public isOpen = false;
    public actionSheet: ActionSheet;
    public isLoading = false;
    public animationState$: Observable<ActionSheetAnimationState>;
    public actionSheetIsFullyClosed$: Observable<boolean>;
    public animationDoneSubject = new Subject<void>();

    constructor(
        private _actionSheetService: ActionSheetService,
        private _backdropService: BackdropService,
        private _hideOpenElementsService: HideOpenElementsService
    ) {
        this.animationState$ = this._animationStateSubject.pipe(
            forLifeOf(this),
            // Angular Animations needs our action sheet to have its full height BEFORE
            // animation begins, so it can do calculations like `{ top: '*' }`.
            // Thanks to `ngStyle`, this happens only when `actionSheetIsFullyClosed$` gets
            // `false`. So, we want our `[@slide]` subscriber to be called AFTER our `ngIf`
            // subscriber, which is why we need this 0ms delay.
            delay(0),
            share()
        );

        this.actionSheetIsFullyClosed$ = this._animationStateSubject.pipe(
            forLifeOf(this),
            switchMap((animationState) =>
                animationState === ActionSheetAnimationState.Closed
                    ? // The action sheet is "fully closed" once the '* => closed' animation has
                      // completed.
                      this.animationDoneSubject.pipe(mapTo(true))
                    : // The action sheet stops being "fully closed" the moment the 'closed => *'
                      // animation begins.
                      of(false)
            ),
            startWith(true),
            distinctUntilChanged(),
            share()
        );
    }

    public ngOnInit(): void {
        this._actionSheetService.actionSheetStream
            .pipe(
                forLifeOf(this),
                throttle(() => this.animationDoneSubject)
            )
            .subscribe((actionSheet) => {
                const hasNewActionSheet = !!actionSheet;
                this.isOpen = hasNewActionSheet;
                if (hasNewActionSheet) {
                    this.actionSheet = actionSheet;
                    const animationState = actionSheet.stickToTop
                        ? ActionSheetAnimationState.OpenTop
                        : ActionSheetAnimationState.Open;
                    this._animationStateSubject.next(animationState);
                    this.openBackDrop();
                } else {
                    this._animationStateSubject.next(ActionSheetAnimationState.Closed);
                    if (this._backdropService.isOpen) {
                        this._backdropService.closeBackDrop();
                    }
                }
            });

        this._actionSheetService.actionSheetLoadingStream
            .pipe(forLifeOf(this))
            .subscribe((isLoading) => {
                this.isLoading = isLoading;
                if (this.isLoading) {
                    this.actionSheet = null;
                    this.isOpen = true;
                    this.openBackDrop();
                }
            });

        this._hideOpenElementsService.hideOpenElementsStream.pipe(forLifeOf(this)).subscribe(() => {
            this.handleCloseClick();
        });
    }

    public ngOnDestroy(): void {}

    private _handleBackdropClick(): void {
        this.handleCloseClick();
    }

    public openBackDrop(): void {
        this._backdropService.openBackdrop({
            onClick: this._handleBackdropClick.bind(this),
            animate: true
        });
    }

    public handleCloseClick(): void {
        if (this.actionSheet) {
            this._actionSheetService.closeActionSheet();
        }
    }
}
